import { useState } from "react";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";
import { ArrowDown } from "@/components/UI/svgs";
import { ActionHandler } from "@/components/UI/tools";
import { EmptyRow, Row, Section } from "../ui";
import { formatValue } from "./format";
import { ChartState, LabelFor, totals, UsageRow, UsageTable, visibleRows } from "./table";

export const usageTypeLabel: LabelFor = (item) => appState.t(`usage.type.${item.key}`);

/** A user's name; the backend keys usage that belongs to no user "system". */
export const userRowLabel: LabelFor = (item) => (item.key === "system" ? appState.t("usage.system") : item.label);

export const unpricedHint = (tokens: number | undefined) =>
    tokens ? appState.t("usage.unpricedTokens", { count: formatValue(tokens, "count") }) : undefined;

/**
 * One chart: the total of the rows shown, folding out into every column's
 * total, followed by the rows themselves.
 */
export const UsageChart = observer(({ chart, terms }: { chart: ChartState; terms: string[] }) => {
    const [open, setOpen] = useState(false);
    const table = chart.table;
    const rows = table ? visibleRows(table, terms) : [];
    const sums = table ? totals(table, rows) : undefined;
    const canOpen = !!sums?.columns.length;
    const hint = chart.loading
        ? appState.t("common.loading")
        : chart.failed
          ? appState.t("usage.failed")
          : chart.hint;

    return (
        <Section title={chart.title} hint={hint}>
            <Row
                onClick={canOpen ? () => setOpen(!open) : undefined}
                selected={open}
                leading={
                    canOpen && (
                        <span className={`fold-chevron ${open ? "" : "-rotate-90"}`}>
                            <ArrowDown size={16} />
                        </span>
                    )
                }
                trailing={<p className="l">{sums && table ? formatValue(sums.value, table.unit) : "–"}</p>}
            >
                <p className="l">{table?.valueLabel ?? appState.t("usage.total")}</p>
            </Row>
            {open &&
                sums?.columns.map((column) => (
                    <Row
                        key={column.key}
                        trailing={<p className="detail-text-strong">{formatValue(column.value, column.unit)}</p>}
                    >
                        <p className="detail-text-strong">{column.label}</p>
                    </Row>
                ))}
            {table && <DataTable table={table} rows={rows} />}
        </Section>
    );
});

const COLLAPSED_ROWS = 5;

const DataTable = observer(({ table, rows }: { table: UsageTable; rows: UsageRow[] }) => {
    const [expanded, setExpanded] = useState(false);
    if (!table.rows.length) return <EmptyRow>{appState.t("usage.empty")}</EmptyRow>;

    const hidden = rows.length - COLLAPSED_ROWS;
    const shown = expanded || hidden <= 0 ? rows : rows.slice(0, COLLAPSED_ROWS);
    const toggle = expanded
        ? appState.t("usage.showFewer")
        : appState.t("usage.showAll", { count: rows.length });

    return (
        <>
            <div className="row-form overflow-x-auto">
                <table className="data-table whitespace-nowrap">
                    <thead>
                        <tr>
                            <th>{table.rowLabel}</th>
                            <th>{table.valueLabel ?? appState.t("usage.total")}</th>
                            {table.columns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((row) => (
                            <tr key={row.key} className={row.value === 0 ? "data-table-row-inactive" : undefined}>
                                <td>{row.label}</td>
                                <td>{formatValue(row.value, table.unit)}</td>
                                {table.columns.map((column) => (
                                    <td key={column.key}>{formatValue(row.cells[column.key] ?? 0, column.unit)}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {hidden > 0 && (
                <div className="row-actions sticky bottom-0 bg-elevated border-t border-line">
                    <ActionHandler title={toggle} funq={() => setExpanded(!expanded)} className="btn-ghost btn-sm">
                        <span className={`fold-chevron ${expanded ? "rotate-180" : ""}`}>
                            <ArrowDown size={14} />
                        </span>
                        {toggle}
                    </ActionHandler>
                </div>
            )}
        </>
    );
});